import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  PhoneAndroid as MpesaIcon,
  CheckCircle as SuccessIcon,
  Cancel as FailedIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { paymentsApi } from '../../api/endpoints/payments';
import { Loan } from '../../api/types/loan.types';

interface Props {
  open: boolean;
  onClose: () => void;
  loan: Loan;
}

type DialogStep = 'form' | 'waiting' | 'success' | 'failed';

const POLL_INTERVAL_MS = 5000;
const TIMEOUT_SECONDS = 90;

/** Normalises any Kenyan phone format to 254XXXXXXXXX */
const normalizePhone = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('0') && digits.length === 10) return '254' + digits.slice(1);
  if (digits.startsWith('254')) return digits;
  if ((digits.startsWith('7') || digits.startsWith('1')) && digits.length === 9)
    return '254' + digits;
  return digits;
};

const isValidKenyanPhone = (phone: string): boolean => {
  const normalized = normalizePhone(phone);
  return /^254[71]\d{8}$/.test(normalized);
};

const formatCurrency = (val: number) =>
  new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(val);

const MpesaPaymentDialog: React.FC<Props> = ({ open, onClose, loan }) => {
  const queryClient = useQueryClient();

  const [step, setStep] = useState<DialogStep>('form');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [amountError, setAmountError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkoutRequestId, setCheckoutRequestId] = useState('');
  const [failedReason, setFailedReason] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(TIMEOUT_SECONDS);

  // Reset whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    const defaultPhone = loan.customerPhoneNumber || loan.customerPhone || '';
    const outstanding = parseFloat(String(loan.amountOutstanding)) || 0;
    setStep('form');
    setPhone(defaultPhone);
    setAmount(outstanding > 0 ? outstanding.toFixed(2) : '');
    setPhoneError('');
    setAmountError('');
    setIsSubmitting(false);
    setCheckoutRequestId('');
    setFailedReason('');
    setSecondsLeft(TIMEOUT_SECONDS);
  }, [open, loan]);

  // Invalidate loan queries on success so details refresh
  const handleSuccess = useCallback(() => {
    setStep('success');
    queryClient.invalidateQueries({ queryKey: ['loan', loan.loanId] });
    queryClient.invalidateQueries({ queryKey: ['loanTransactions', loan.loanId] });
    queryClient.invalidateQueries({ queryKey: ['loans'] });
  }, [queryClient, loan.loanId]);

  // Poll M-Pesa query endpoint while waiting
  useEffect(() => {
    if (step !== 'waiting' || !checkoutRequestId) return;

    const poll = async () => {
      try {
        const result = await paymentsApi.mpesaQuery({ checkoutRequestId });
        const isSuccess =
          result.status === 'SUCCESS' || result.resultCode === '0';
        const isFailed =
          result.status === 'FAILED' ||
          (result.resultCode !== undefined &&
            result.resultCode !== '0' &&
            result.resultCode !== '');

        if (isSuccess) {
          handleSuccess();
        } else if (isFailed) {
          setStep('failed');
          setFailedReason(result.resultDesc || 'Payment was cancelled or failed.');
        }
      } catch {
        // Silent — keep polling; server may be momentarily unavailable
      }
    };

    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [step, checkoutRequestId, handleSuccess]);

  // Countdown + auto-fail when prompt expires
  useEffect(() => {
    if (step !== 'waiting') return;

    if (secondsLeft <= 0) {
      setStep('failed');
      setFailedReason('The M-Pesa prompt timed out. The customer did not respond in time.');
      return;
    }

    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [step, secondsLeft]);

  const validateForm = (): boolean => {
    let valid = true;

    if (!isValidKenyanPhone(phone)) {
      setPhoneError('Enter a valid Kenyan number, e.g. 0712 345 678 or 254712345678');
      valid = false;
    } else {
      setPhoneError('');
    }

    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      setAmountError('Enter an amount greater than 0');
      valid = false;
    } else {
      setAmountError('');
    }

    return valid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const response = await paymentsApi.mpesaSTKPush({
        loanId: loan.loanId,
        customerId: loan.customerId,
        phoneNumber: normalizePhone(phone),
        amount: parseFloat(amount).toFixed(2),
      });

      if (!response.checkoutRequestId) {
        throw new Error(response.message || 'No checkoutRequestId returned');
      }

      setCheckoutRequestId(response.checkoutRequestId);
      setSecondsLeft(TIMEOUT_SECONDS);
      setStep('waiting');
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to send M-Pesa prompt. Please try again.';
      setAmountError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Prevent accidental close while the STK prompt is active
    if (step === 'waiting') return;
    onClose();
  };

  const handleTryAgain = () => {
    setStep('form');
    setFailedReason('');
  };

  const outstanding = parseFloat(String(loan.amountOutstanding)) || 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MpesaIcon color="success" />
        Pay via M-Pesa
      </DialogTitle>

      <DialogContent dividers>
        {/* ── FORM ── */}
        {step === 'form' && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              The customer will receive an M-Pesa PIN prompt on their phone.
              Once they enter their PIN, payment is confirmed automatically.
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Loan outstanding:{' '}
              <Typography component="span" fontWeight="bold" color="error.main">
                {formatCurrency(outstanding)}
              </Typography>
            </Typography>

            <TextField
              label="Customer Phone Number"
              fullWidth
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setPhoneError('');
              }}
              error={!!phoneError}
              helperText={phoneError || 'Format: 0712 345 678 or 254712345678'}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">+</InputAdornment>
                ),
              }}
            />

            <TextField
              label="Amount to Pay (KES)"
              fullWidth
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setAmountError('');
              }}
              error={!!amountError}
              helperText={amountError}
              type="number"
              inputProps={{ min: 1, step: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">KES</InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        {/* ── WAITING ── */}
        {step === 'waiting' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
              gap: 3,
            }}
          >
            <CircularProgress size={72} color="success" thickness={4} />

            <Box textAlign="center">
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Waiting for Payment
              </Typography>
              <Typography color="text.secondary">
                An M-Pesa prompt was sent to{' '}
                <strong>{normalizePhone(phone)}</strong>.
                <br />
                Ask the customer to check their phone and enter their M-Pesa
                PIN.
              </Typography>
            </Box>

            <Box
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 2,
                px: 4,
                py: 1.5,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Prompt expires in
              </Typography>
              <Typography
                variant="h4"
                fontWeight="bold"
                color={secondsLeft < 20 ? 'error.main' : 'text.primary'}
              >
                {secondsLeft}s
              </Typography>
            </Box>

            <Alert severity="warning" sx={{ width: '100%' }}>
              Do not close this dialog — we are checking for payment
              confirmation every {POLL_INTERVAL_MS / 1000} seconds.
            </Alert>
          </Box>
        )}

        {/* ── SUCCESS ── */}
        {step === 'success' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
              gap: 2,
            }}
          >
            <SuccessIcon sx={{ fontSize: 80, color: 'success.main' }} />
            <Typography variant="h6" fontWeight="bold" color="success.main">
              Payment Confirmed!
            </Typography>
            <Typography color="text.secondary" textAlign="center">
              M-Pesa payment of{' '}
              <strong>{formatCurrency(parseFloat(amount))}</strong> was
              successfully received.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The loan balance has been updated automatically.
            </Typography>
          </Box>
        )}

        {/* ── FAILED ── */}
        {step === 'failed' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
              gap: 2,
            }}
          >
            <FailedIcon sx={{ fontSize: 80, color: 'error.main' }} />
            <Typography variant="h6" fontWeight="bold" color="error.main">
              Payment Not Received
            </Typography>
            <Alert severity="error" sx={{ width: '100%' }}>
              {failedReason}
            </Alert>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              No payment was recorded. You can try again or ask the customer to
              use a different method.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {step === 'form' && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <MpesaIcon />
                )
              }
            >
              {isSubmitting ? 'Sending…' : 'Send M-Pesa Prompt'}
            </Button>
          </>
        )}

        {step === 'waiting' && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
            Checking every {POLL_INTERVAL_MS / 1000}s…
          </Typography>
        )}

        {step === 'success' && (
          <Button variant="contained" color="success" onClick={onClose}>
            Done
          </Button>
        )}

        {step === 'failed' && (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button variant="outlined" color="success" onClick={handleTryAgain}>
              Try Again
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MpesaPaymentDialog;
